import { startTransition, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useSearchFilters } from '@rocco/ui/filter-sort/hooks/use-search-filters';
import type {
    AggregationArgs,
    LocationServiceOptions,
} from '@rocco/ui/filter-sort/hooks/use-search-filters';
import { isFilterAggregation } from '@rocco/ui/filter-sort/hooks/use-search-filters';
import type { FilterStates } from '@modules-rocco/filters/types/filter-states';
import { mapFilterStatesToURL } from '@modules-rocco/filters/data-mappers/filter-states-to-url';
import { toggleMultipleListFilterStateItem } from '@modules-rocco/filters/filter-state/multiple-values-filter-state';
import { toggleSearchableListFilterStateItem } from '@modules-rocco/filters/filter-state/searchable-list-filter-state';
import { useFeatureFlags, useRootData } from '@modules/root';
import { ARTICLES_LOAD_MORE_API_ROUTE } from '~/routes/remix-api.articles.load-more';
import { ORDER_PARAM } from '@modules-rocco/articles/config/article-facets';
import { ARTICLES_REVAMP_FILTERS_CONFIG } from '../config/filter.config';

/**
 * Location is not one of this listing's facets, so the service is inert rather
 * than wired to the place finder — the hook requires it either way.
 */
const inertLocationService = (
    country: LocationServiceOptions['country'],
): LocationServiceOptions => ({
    country,
    currentLocation: {
        isLoading: false,
        location: null,
        onCurrentLocationFetch: () => {},
    },
    keywordLocations: {
        isLoading: false,
        locations: null,
        onKeywordLocationsFetch: () => {},
        onKeywordResetPlaces: () => {},
    },
});

/**
 * Filter state for the revamped articles listing.
 *
 * The parallel of `useCategoryFilterSort`, but off the search service: this
 * listing is served by the CMS article category query, so a staged count has to
 * come from that same query or it would disagree with the grid. The refine
 * sheet's count therefore rides the listing's own paging endpoint at page size
 * one, and no Elasticsearch aggregation is requested at all.
 *
 * Sort is not offered — the category query orders by publish date and nothing
 * on the page can change that, so an empty option list is the honest one (the
 * bar hides the shared sort dropdown on the revamp listing).
 */
export const useArticlesFilterSort = (initialFilterStates: FilterStates) => {
    const { localisation } = useRootData();
    const location = useLocation();
    const navigate = useNavigate();
    const featureFlags = useFeatureFlags();

    const currentURL = location.pathname + location.search;

    const buildDestinationURL = useCallback(
        (states: FilterStates) => {
            const next = mapFilterStatesToURL(
                states,
                currentURL,
                undefined,
                undefined,
                featureFlags,
            );
            // The shared mapper rebuilds the URL from filter states alone, so
            // anything it does not model is dropped. Order is chosen in the
            // same drawer as the facets, so losing it on every facet toggle
            // would silently reset the listing under the reader.
            const order = new URLSearchParams(location.search).get(ORDER_PARAM);
            if (!order) return next;

            const [path, query = ''] = next.split('?');
            const params = new URLSearchParams(query);
            params.set(ORDER_PARAM, order);
            return `${path}?${params.toString()}`;
        },
        [currentURL, featureFlags, location.search],
    );

    const handleRefetch = useCallback(
        (states: FilterStates) => {
            const nextURL = buildDestinationURL(states);
            if (nextURL === currentURL) return;
            // Refining holds the reader's place — the same rule the other
            // directories navigate under.
            startTransition(() => {
                navigate(nextURL, { preventScrollReset: true });
            });
        },
        [buildDestinationURL, currentURL, navigate],
    );

    /**
     * The count a commit of these staged filters would produce.
     *
     * Only `totalCount` is read back (the option lists are a fixed vocabulary,
     * so there is nothing else to rebuild), which is why one page-size-one
     * request is enough.
     */
    const handleAggregate = useCallback(
        (args: AggregationArgs) => {
            if (!isFilterAggregation(args)) return;

            const destination = buildDestinationURL(args.filterStates);
            args.onStart();

            fetch(
                `${ARTICLES_LOAD_MORE_API_ROUTE}?link=${encodeURIComponent(destination)}&page=1&pageSize=1`,
            )
                .then((response) => (response.ok ? response.json() : null))
                .then((payload: { totalCount?: number } | null) => {
                    args.onSuccess({
                        ...args.filterStates,
                        // Falling back to the count already on screen keeps the
                        // sheet's apply CTA enabled — it is disabled at zero.
                        totalCount:
                            payload?.totalCount ??
                            args.filterStates.totalCount ??
                            0,
                    });
                })
                .catch(() => {
                    args.onSuccess(args.filterStates);
                })
                .finally(() => args.onFinish());
        },
        [buildDestinationURL],
    );

    const searchFilters = useSearchFilters({
        config: ARTICLES_REVAMP_FILTERS_CONFIG,
        initialStates: initialFilterStates,
        isCategoryPage: true,
        onRefetch: handleRefetch,
        onAggregate: handleAggregate,
        // Nothing is queued — every aggregate is a single in-flight request.
        onAggregateQueueClear: () => {},
        onToggleMultipleValues: toggleMultipleListFilterStateItem,
        onToggleSearchableList: toggleSearchableListFilterStateItem,
        locationService: inertLocationService(localisation.country),
    });

    return useMemo(() => ({ searchFilters }), [searchFilters]);
};
